/**
 * Set a password from an invite or reset link.
 *
 * This is where the link `npm run bootstrap:owner` prints lands, and where
 * every invited user starts (plan §6.10: invite → password → MFA → PIN). The
 * stages after the password are in `set-up.tsx`, which this hands over to.
 *
 * **The link is read before anything is typed** (Aaron, step 27). It asks
 * `POST /auth/set-password/about` what it is for — the company, the address
 * and every stage — so the page can draw the same stages `/set-up` will, and
 * so an expired or spent link says so on arrival rather than after somebody
 * has chosen a password twice. Reading it spends nothing.
 */
import { useEffect, useState } from 'react'
import {
  ApiError, PASSWORD_MIN_LENGTH, type SetPasswordAbout,
} from '@rowted/shared'
import { request, setCsrfToken } from '../lib/api.js'
import { Button } from '../components/primitives.js'
import { SetupProgress } from '../components/setup-progress.js'
import { RowtedLogo } from '../frame/logo.js'

type Loaded =
  | { at: 'reading' }
  | { at: 'ready'; about: SetPasswordAbout }
  | { at: 'refused'; expired: boolean; message: string }

export function SetPassword() {
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const [loaded, setLoaded] = useState<Loaded>({ at: 'reading' })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) return
    let live = true
    request<SetPasswordAbout>('/auth/set-password/about', { method: 'POST', body: { token } })
      .then((about) => { if (live) setLoaded({ at: 'ready', about }) })
      .catch((caught: unknown) => {
        if (!live) return
        if (caught instanceof ApiError
          && (caught.code === 'token_expired' || caught.code === 'token_invalid')) {
          setLoaded({
            at: 'refused', expired: caught.code === 'token_expired', message: caught.message,
          })
          return
        }
        /*
         * Anything else — the network, a server mid-deploy — is not a verdict
         * on the link, so the form still draws and the submit is what finds
         * out. A page that said "this link is dead" because a request blinked
         * would send somebody to ask for a new one they did not need.
         */
        setLoaded({
          at: 'ready',
          about: { kind: 'invite', company: 'Rowted', email: '', steps: ['password'] },
        })
      })
    return () => { live = false }
  }, [token])

  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH
  const mismatched = confirm.length > 0 && confirm !== password
  const ready = password.length >= PASSWORD_MIN_LENGTH && confirm === password && !busy

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)

    try {
      const result = await request<{ csrfToken: string }>('/auth/set-password', {
        method: 'POST',
        body: { token, password },
      })
      setCsrfToken(result.csrfToken)
      // The password is done; `/set-up` runs whichever stages are left and
      // sends them on when there are none.
      window.location.assign('/set-up')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!token) {
    return (
      <Centered>
        <RowtedLogo />
        <h1 className="m-0 text-title">That link is incomplete</h1>
        <p className="m-0 text-cell text-text-muted">
          Open the link from your invite email again, or ask for a new one.
        </p>
      </Centered>
    )
  }

  if (loaded.at === 'reading') {
    return (
      <Centered>
        <RowtedLogo />
        <p className="m-0 text-cell text-text-muted">Checking your link…</p>
      </Centered>
    )
  }

  if (loaded.at === 'refused') {
    return (
      <Centered>
        <RowtedLogo />
        <div>
          <h1 className="m-0 text-title">
            {loaded.expired ? 'That link has expired' : 'That link no longer works'}
          </h1>
          <p className="m-0 mt-1 text-cell leading-[1.45] text-text-muted">
            {/*
              * Who can send another, said plainly: an invite is resent by
              * whoever sent it, and a reset is asked for again from the
              * sign-in page. A newer link also retires this one, which is the
              * commonest reason a link that was fine yesterday is not.
              */}
            Links work once, and a newer one retires the last. Ask whoever invited you to
            send it again — or, if you were resetting your password, ask for a new link
            from the sign-in page.
          </p>
        </div>
        <Button variant="primary" size="big" onClick={() => window.location.assign('/sign-in')}>
          Go to sign in
        </Button>
      </Centered>
    )
  }

  const { about } = loaded
  const journey = about.steps.length > 1

  return (
    <Centered>
      <form onSubmit={submit} className="flex w-full flex-col gap-4">
        <RowtedLogo />

        <SetupProgress company={about.company} steps={about.steps} at={0} />

        <div>
          <h1 className="m-0 text-title">Choose a password</h1>
          <p className="m-0 mt-1 text-cell leading-[1.45] text-text-muted">
            {about.email && <>For <b className="font-semibold text-text">{about.email}</b>. </>}
            At least {PASSWORD_MIN_LENGTH} characters. A short phrase you will remember beats
            something clever you will not.
          </p>
        </div>

        {/* The address, for a password manager to file this under. */}
        {about.email && (
          <input type="email" name="username" value={about.email} autoComplete="username"
            readOnly hidden />
        )}

        <Field
          label="Password"
          hint={tooShort ? `${PASSWORD_MIN_LENGTH - password.length} more to go` : undefined}
        >
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            autoFocus
            required
            className="h-10 w-full rounded-button border border-line bg-field px-3 text-body"
          />
        </Field>

        <Field label="Confirm" hint={mismatched ? 'These do not match yet' : undefined}>
          <input
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
            className="h-10 w-full rounded-button border border-line bg-field px-3 text-body"
          />
        </Field>

        {error && (
          <p role="alert" className="m-0 rounded-button bg-danger-bg px-3 py-2 text-cell text-danger">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" size="big" disabled={!ready}>
          {busy ? 'Setting…' : journey ? 'Set password and continue' : 'Set password and sign in'}
        </Button>
      </form>
    </Centered>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-app items-center justify-center bg-bg p-4">
      <div className="flex w-full max-w-[420px] flex-col gap-4 rounded-card border border-line bg-surface p-6">
        {children}
      </div>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between">
        <span className="text-tag uppercase tracking-[0.06em] text-text-faint">{label}</span>
        {hint && <span className="text-sub text-warn">{hint}</span>}
      </span>
      {children}
    </label>
  )
}
